<template>
    <div class="field-selector-picker">
        <!-- 輸入框 -->
        <div class="picker-input" @click="openDrawer">
            <Icon type="md-business" class="input-icon" />
            <span class="value-display" :class="{ placeholder: !selectedField }">
                {{ selectedField ? selectedField.name : placeholder }}
            </span>
            <Icon type="ios-arrow-forward" class="arrow-icon" />
        </div>

        <!-- 選擇器 Drawer -->
        <Drawer
            v-model="showDrawer"
            title="選擇案場"
            :width="ui.drawerWidth"
            placement="right"
            :closable="true"
            class-name="drawer-fullbody"
            :styles="{ zIndex: drawerZIndex }"
        >
            <div class="drawer-content">
                <!-- 當前選擇預覽 -->
                <div class="selection-preview">
                    <div class="preview-info">
                        <div class="preview-label">目前選擇</div>
                        <div class="preview-value">{{ selectedField ? selectedField.name : '尚未選擇' }}</div>
                        <div class="preview-hint" v-if="currentFilterSummary">
                            <Icon type="md-funnel" />
                            <span>{{ currentFilterSummary }}</span>
                        </div>
                        <div class="preview-hint" v-else-if="selectedField?.address">
                            <Icon type="md-pin" />
                            <span>{{ selectedField.address }}</span>
                        </div>
                    </div>
                    <Button size="small" class="filter-btn" @click="openFilterDrawer">
                        <Icon type="md-funnel" />
                        範圍
                    </Button>
                </div>

                <!-- 搜尋框 -->
                <div class="search-box">
                    <Input
                        v-model="searchKeyword"
                        placeholder="搜尋案場名稱或地址..."
                        clearable
                        @on-clear="searchKeyword = ''"
                        prefix="ios-search"
                    />
                </div>

                <!-- 案場列表 -->
                <div class="field-list">
                    <div v-if="filteredFields.length === 0" class="empty-list">
                        <Icon type="ios-folder-open" size="48" color="#ccc" />
                        <p>{{ searchKeyword ? '找不到符合的案場' : '暫無案場資料' }}</p>
                    </div>
                    <div
                        v-else
                        v-for="field in filteredFields"
                        :key="field.id"
                        class="field-item"
                        :class="{ selected: selectedField?.id === field.id }"
                        @click="selectField(field)"
                    >
                        <div class="field-info">
                            <div class="field-name">{{ field.name }}</div>
                            <div class="field-address" v-if="field.address">{{ field.address }}</div>
                        </div>
                        <Icon v-if="selectedField?.id === field.id" type="md-checkmark" class="check-icon" />
                    </div>
                </div>
            </div>
        </Drawer>

        <!-- 案場過濾 Drawer -->
        <FieldFilterDrawer
            ref="filterDrawer"
            v-model:modelType="internalFilterType"
            v-model:modelCity="internalFilterCity"
        />
    </div>
</template>

<script>
import FieldFilterDrawer from './FieldFilterDrawer.vue';
import { fieldState } from '@shared/stores/fieldScope';
import { uiState } from '@shared/stores/uiState';

export default {
    name: 'FieldSelectorDrawer',
    components: {
        FieldFilterDrawer
    },
    props: {
        modelValue: {
            type: Object,
            default: null
        },
        placeholder: {
            type: String,
            default: '請選擇案場'
        },
        fields: {
            type: Array,
            default: () => []
        },
        filterType: {
            type: String,
            default: ''
        },
        filterCity: {
            type: String,
            default: ''
        }
    },
    emits: ['update:modelValue', 'change'],
    data() {
        return {
            showDrawer: false,
            searchKeyword: '',
            selectedField: this.modelValue,
            internalFilterType: this.filterType,
            internalFilterCity: this.filterCity,
            drawerZIndex: 0
        };
    },
    computed: {
        // 使用 Pinia store（支援獨立模式）
        fieldScope() {
            return fieldState();
        },
        ui() {
            return uiState();
        },
        fieldList() {
            // 優先使用 props 傳入的 fields
            if (this.fields && this.fields.length > 0) {
                return this.fields;
            }
            // 使用完整的 fields 列表，由 filteredByConditions 進行過濾
            return this.fieldScope?.fields || [];
        },
        filterTypes() {
            const filterTypeList = this.fieldScope?.filterTypeList || [];
            return filterTypeList.filter(t => t.key !== 'hr');
        },
        currentFilterSummary() {
            const parts = [];
            if (this.internalFilterType) {
                const type = this.filterTypes.find(t => t.key === this.internalFilterType);
                if (type) parts.push(type.title);
            }
            if (this.internalFilterCity) {
                parts.push(this.internalFilterCity);
            }
            return parts.length > 0 ? parts.join(' · ') : '';
        },
        filteredByConditions() {
            let result = this.fieldList;

            // 使用內部狀態進行過濾（空字串表示「全部」，不應 fallback）
            const activeType = this.internalFilterType !== undefined ? this.internalFilterType : this.filterType;
            const activeCity = this.internalFilterCity !== undefined ? this.internalFilterCity : this.filterCity;

            if (activeType) {
                result = result.filter(field => {
                    if (!field.type || !Array.isArray(field.type)) return false;
                    return field.type.includes(activeType);
                });
            }

            if (activeCity) {
                const normalizeCity = (city) => city ? city.replace(/臺/g, '台') : '';
                const targetCity = normalizeCity(activeCity);
                result = result.filter(field => {
                    const fieldCity = normalizeCity(field.city);
                    return fieldCity && fieldCity.includes(targetCity);
                });
            }

            return result;
        },
        filteredFields() {
            let result = this.filteredByConditions;

            if (this.searchKeyword) {
                const keyword = this.searchKeyword.toLowerCase();
                result = result.filter(field =>
                    field.name?.toLowerCase().includes(keyword) ||
                    field.address?.toLowerCase().includes(keyword)
                );
            }

            return result;
        }
    },
    watch: {
        modelValue: {
            handler(val) {
                this.selectedField = val;
            },
            immediate: true
        },
        showDrawer(newVal) {
            // 監聽 showDrawer 變化來管理 z-index
            if (!newVal && this.drawerZIndex > 0) {
                // 關閉時釋放 z-index
                this.ui?.releaseZIndex?.('selector', 'field');
                this.drawerZIndex = 0;
            }
        }
    },
    methods: {
        openDrawer() {
            this.searchKeyword = '';
            // 獲取 z-index
            this.drawerZIndex = this.ui?.getNextZIndex?.('selector', 'field') || 1000;
            this.showDrawer = true;
        },
        closeDrawer() {
            this.showDrawer = false;
            // z-index 釋放由 watch 處理
        },
        selectField(field) {
            this.selectedField = field;
            this.$emit('update:modelValue', field);
            this.$emit('change', field);
            this.showDrawer = false;
        },
        openFilterDrawer() {
            this.$refs.filterDrawer?.openDrawer();
        }
    }
};
</script>

<style lang="stylus" scoped>
.field-selector-picker
    width 100%

.picker-input
    display flex
    align-items center
    padding 0 10px
    height 32px
    background #fff
    border 1px solid #dcdee2
    border-radius 4px
    cursor pointer
    transition all 0.2s

    &:hover
        border-color #57a3f3

.input-icon
    color #808695
    margin-right 8px
    flex-shrink 0

.value-display
    flex 1
    font-size 13px
    color #515a6e
    overflow hidden
    text-overflow ellipsis
    white-space nowrap

    &.placeholder
        color #c5c8ce

.arrow-icon
    color #c5c8ce
    flex-shrink 0
    margin-left 4px

// Drawer 內容
.drawer-content
    display flex
    flex-direction column
    height 100%
    overflow hidden

.selection-preview
    padding 16px
    background linear-gradient(135deg, #667eea 0%, #764ba2 100%)
    color #fff
    flex-shrink 0
    display flex
    align-items center
    justify-content space-between

.preview-info
    flex 1
    min-width 0

.preview-label
    font-size 12px
    opacity 0.8
    margin-bottom 4px

.preview-value
    font-size 18px
    font-weight 600
    overflow hidden
    text-overflow ellipsis
    white-space nowrap

.preview-hint
    display flex
    align-items center
    gap 6px
    margin-top 8px
    font-size 12px
    opacity 0.85

    .ivu-icon
        font-size 14px

.filter-btn
    flex-shrink 0
    margin-left 16px
    background rgba(255, 255, 255, 0.2) !important
    border-color rgba(255, 255, 255, 0.4) !important
    color #fff !important

    &:hover
        background rgba(255, 255, 255, 0.3) !important
        border-color rgba(255, 255, 255, 0.6) !important

    .ivu-icon
        margin-right 4px

.search-box
    padding 12px 16px
    background #f8f8f9
    flex-shrink 0

.field-list
    flex 1
    overflow-y auto
    padding 8px

    &::-webkit-scrollbar
        width 4px

    &::-webkit-scrollbar-thumb
        background rgba(0, 0, 0, 0.15)
        border-radius 2px

.empty-list
    text-align center
    padding 40px 20px
    color #999

    p
        margin-top 10px

.field-item
    display flex
    align-items center
    padding 12px
    border-radius 6px
    cursor pointer
    transition all 0.15s

    &:hover
        background rgba(45, 140, 240, 0.08)

    &.selected
        background rgba(45, 140, 240, 0.12)

        .field-name
            color #2d8cf0
            font-weight 500

.field-info
    flex 1
    min-width 0

.field-name
    font-size 14px
    color #333
    margin-bottom 2px
    overflow hidden
    text-overflow ellipsis
    white-space nowrap

.field-address
    font-size 12px
    color #999
    overflow hidden
    text-overflow ellipsis
    white-space nowrap

.check-icon
    color #2d8cf0
    font-size 18px
    flex-shrink 0
    margin-left 8px

// ===== Dark Theme =====
body.dark-theme
    .picker-input
        background #1a1d24
        border-color #3b4252

        &:hover
            border-color #5A9FFF

    .input-icon
        color #8892a0

    .value-display
        color #e5e9f0

        &.placeholder
            color #6a7385

    .arrow-icon
        color #6a7385

    .search-box
        background #252932

    .field-list
        &::-webkit-scrollbar-thumb
            background rgba(255, 255, 255, 0.2)

            &:hover
                background rgba(255, 255, 255, 0.35)

    .field-item
        &:hover
            background #3b4252

        &.selected
            background rgba(90, 159, 255, 0.15)

            .field-name
                color #5A9FFF

    .field-name
        color #e5e9f0

    .field-address
        color #8892a0

    .check-icon
        color #5A9FFF

    .empty-list
        color #8892a0
</style>
